/*
  Indian Overseas Bank QR card for the NEW mold (IOBNewQRCard) - printed at 84.3mm x 133mm.
  Same elements as the old-mold card (IOBQRCard), re-arranged for the shorter
  new-mold page: the logo now sits in its own white box on the tricolour frame,
  and the merchant name moved above "SCAN & PAY" so the QR, UPI ID and the
  accepted-app strip (/assets/IOB/iob-bottom-logogroup.svg) fit underneath.
  Sizes are tuned for the 350 x 552 px capture BulkQRDialog renders this card at.
*/
import React, { useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import QRCodeGenerator from './QRCodeGenerator';
import FitMerchantName from './FitMerchantName';

// Card frame. The slight slant is what the printed card uses, so the stops
// are angled rather than a straight vertical split.
const TRICOLOUR = `linear-gradient(
  195deg,
  #FF9933 0%,
  #FF9933 50%,
  #FFFFFF 50%,
  #FFFFFF 56%,
  #138808 56%,
  #138808 100%
)`;

/*
  QR frame. Straight 180deg, not the card's 195deg: on a box this small the
  slant meets the left and right edges at visibly different heights, so the
  white band reads as a broken step where orange and green meet. A level
  split keeps the seam straight on both sides.
*/
const QR_FRAME_TRICOLOUR = `linear-gradient(
  180deg,
  #FF9933 0%,
  #FF9933 48%,
  #FFFFFF 48%,
  #FFFFFF 52%,
  #138808 52%,
  #138808 100%
)`;

const SCAN_AND_PAY_BLUE = '#3B82C4';

export default function IobNewScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  return (
    <Box
      ref={cardRef}
      sx={{
        width: '100%',
        height: '100%',
        background: '#044E9D',
        borderRadius: '28px',
        px: 1,
        pt: 1.75,
        pb: 1, // Thicker bottom green border
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Header - bank logo in its own white box on the orange band */}
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          px: 1.25,
          py: 0.8,
          backgroundColor: '#044E9D',
          borderRadius: '18px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          boxSizing: 'border-box'
        }}
      >
        <Box
          component="img"
          src="/assets/IOB/iob-logo.svg"
          alt="Indian Overseas Bank"
          sx={{ width: '70%', height: 'auto', objectFit: 'contain', display: 'block' }}
        />
      </Box>

      {/* White panel */}
      <Box
        sx={{
          mt: 0.75,
          flex: 1,
          minHeight: 0,
          backgroundColor: '#ffffff',
          borderRadius: '22px',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {/* Merchant Name */}
        <FitMerchantName
          text={merchantName}
          maxFontSize={16}
          minFontSize={11}
          maxLines={2}
          lineHeight={1.15}
          fontWeight={700}
          containerSx={{ width: '100%' }}
        />

        {/* Scan & Pay */}
        <Typography
          align="center"
          sx={{
            mt: '-4px',
            color: SCAN_AND_PAY_BLUE,
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: 20,
            lineHeight: 1.15,
            flexShrink: 0,
            mb: 1
          }}
        >
          Scan &amp; Pay
        </Typography>

        {/* QR Code - tricolour border around a white box */}
        <Box
          sx={{
            mt: 1,
            p: '4px',
            width: '76%',
            borderRadius: '16px',
            background: QR_FRAME_TRICOLOUR,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              flex: 1,
              backgroundColor: '#ffffff',
              borderRadius: '13px',
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '& svg': { width: '100%', height: 'auto', display: 'block' }
            }}
          >
            <QRCodeGenerator qrString={qrString} size={190} />
          </Box>
        </Box>

        {/* UPI ID */}
        <Typography
          align="center"
          sx={{
            mt: 0.25,
            color: 'black',
            fontWeight: 600,
            fontFamily: 'Montserrat',
            fontSize: 13,
            lineHeight: 1.3,
            wordBreak: 'break-all',
            flexShrink: 0
          }}
        >
          {merchantVpa}
        </Typography>

        <Divider
          sx={{
            mt: 1,
            mb: 2,
            backgroundColor: 'grey',
            height: '1.5px',
            width: '88%',
            flexShrink: 0
          }}
        />

        {/* Footer - BHIM/UPI and the accepted payment apps */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            component="img"
            src="/assets/IOB/iob-bottom-logogroup.svg"
            alt="BHIM UPI accepted here"
            sx={{
              width: '60%',
              maxHeight: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
